import { Alert } from "@mui/material";
import type { ReactNode } from "react";
import { useCurrentUser } from "@/domains/profile/hooks/useCurrentUser";
import { useDayDismissal } from "@/shared/hooks/useDayDismissal";
import { CompactChromeRow } from "@/shared/ui/mobileChrome";
import { systemBannerSx } from "@/shared/ui/systemBannerStyles";

/**
 * App-wide banner shown when the current user's firm is at or near its monthly
 * email-parsing quota. Driven by the `/me` payload (refetched per the currentUser
 * query's staleTime). Renders nothing when no caps are configured or usage is below
 * the soft cap. Dismissed state is persisted to localStorage and resets each calendar day.
 */
export default function QuotaBanner() {
  const [dismissed, dismiss] = useDayDismissal("tower:quota-banner:dismissed");
  const { data: currentUser } = useCurrentUser();
  const quota = currentUser?.emailQuota;

  if (dismissed || !quota || (!quota.softCapReached && !quota.hardCapReached)) {
    return null;
  }

  const used = quota.currentMonthCount.toLocaleString();

  if (quota.hardCapReached) {
    const cap = quota.hardCap?.toLocaleString() ?? "";
    return (
      <QuotaAlert severity="error" onClose={dismiss}>
        Monthly email limit reached ({used}
        {cap ? ` / ${cap}` : ""}). New emails are received and stored, but are not
        being parsed for the rest of the month. Contact your administrator to raise
        the limit.
      </QuotaAlert>
    );
  }

  // Soft cap crossed. When a hard cap is configured this is a genuine "approaching
  // the limit" warning; without one the soft cap is only an advisory threshold —
  // parsing never stops, so we must not imply a limit is being hit.
  if (quota.hardCap != null) {
    const cap = quota.hardCap.toLocaleString();
    return (
      <QuotaAlert severity="warning" onClose={dismiss}>
        Approaching your monthly email limit ({used} / {cap}).
      </QuotaAlert>
    );
  }

  const cap = quota.softCap?.toLocaleString() ?? "";
  return (
    <QuotaAlert severity="info" onClose={dismiss}>
      Monthly email soft cap reached ({used}
      {cap ? ` / ${cap}` : ""}).
    </QuotaAlert>
  );
}

function QuotaAlert({
  severity,
  onClose,
  children,
}: Readonly<{
  severity: "error" | "warning" | "info";
  onClose: () => void;
  children: ReactNode;
}>) {
  return (
    <CompactChromeRow>
      <Alert severity={severity} square sx={systemBannerSx} onClose={onClose}>
        {children}
      </Alert>
    </CompactChromeRow>
  );
}
